import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "chip" | "chip-primary";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  children: ReactNode;
}

const BASE =
  "inline-flex items-center justify-center gap-2 font-bold transition-colors " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verity-ring " +
  "disabled:cursor-not-allowed disabled:opacity-50";

const CHIP =
  "relative h-[38px] shrink-0 rounded-full border-[1.5px] bg-surface px-4 text-[13.5px] " +
  "text-verity-700 whitespace-nowrap active:scale-[.97] active:bg-verity-050 " +
  "after:absolute after:inset-x-0 after:-inset-y-1 after:rounded-full after:content-['']";

const VARIANT = {
  // One primary action visible at a time. The borrower flow is a sequence of
  // single decisions, and two equally-weighted buttons make it a puzzle.
  primary:
    "w-full rounded-lg bg-verity-600 px-6 py-4 text-base text-white " +
    "shadow-[var(--e-agent)] hover:bg-verity-700 active:bg-verity-700",
  secondary: "w-full py-2 text-base text-ink-600 hover:text-ink-900",
  ghost:
    "rounded-full border border-border-soft bg-surface px-4 py-2 text-sm " +
    "text-ink-600 hover:border-verity-300 hover:text-verity-700",
  // A suggested reply (FR-4): something the borrower might say next, riding on
  // the composer. Outlined, never filled — filled purple is reserved for the
  // borrower's own sent messages, so a suggestion never impersonates one. The
  // ::after box grows the hit area to 44px without growing the chip.
  chip: CHIP + " border-verity-200",
  // First position, and a firmer ring. That is the whole difference: one
  // primary per surface, said with weight rather than with fill.
  "chip-primary": CHIP + " border-verity-ring",
} as const;

export function Button({ variant = "primary", className = "", ...props }: ButtonProps) {
  return <button className={`${BASE} ${VARIANT[variant]} ${className}`} {...props} />;
}
